import React, { useState } from "react";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const ChangePassword = ({ changePassword, logOut }) => {
  const [isUpdateLoading, setUpdateLoading] = useState(false);

  const handleResetPassword = async (values) => {
    try {
      setUpdateLoading(true);
      const responseData = await changePassword({
        password: values.newPassword,
      });

      if (responseData.success) {
        toast.success(responseData.message);
        await logOut();
      } else {
        toast.error(responseData.message);
      }
    } catch (err) {
      toast.error(err?.error || err?.message || "Unable to update password");
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="p-8 sm:p-10">
          <div className="flex flex-col items-center mb-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 mb-4">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V8a4 4 0 118 0v3" />
              </svg>
            </div>
            <h1 className="mt-2 text-2xl lg:text-3xl font-bold text-slate-900">
              Change Password
            </h1>
            <p className="mt-2 text-sm text-slate-500 text-center max-w-xs">
              Please enter your new password below. You will be logged out after a successful update.
            </p>
          </div>

          <Formik
            initialValues={{
              newPassword: "",
              confirmPassword: "",
            }}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              newPassword: Yup.string().required("New password is required"),
              confirmPassword: Yup.string()
                .required("Confirm password is required")
                .oneOf([Yup.ref("newPassword"), null], "Both passwords need to be the same"),
            })}
            onSubmit={(values) => handleResetPassword(values)}
          >
            {({ errors, touched }) => (
              <Form className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    New Password
                  </label>
                  <Field
                    name="newPassword"
                    type="password"
                    placeholder="Enter new password"
                    className={`w-full rounded-xl border px-4 py-3 text-slate-700 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                      errors.newPassword && touched.newPassword
                        ? "border-red-500 focus:ring-red-200"
                        : "border-slate-300 focus:border-black focus:ring-slate-200"
                    }`}
                  />
                  {errors.newPassword && touched.newPassword && (
                    <p className="mt-2 text-xs font-medium text-red-500">
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Confirm Password
                  </label>
                  <Field
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    className={`w-full rounded-xl border px-4 py-3 text-slate-700 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 transition-all duration-200 ${
                      errors.confirmPassword && touched.confirmPassword
                        ? "border-red-500 focus:ring-red-200"
                        : "border-slate-300 focus:border-black focus:ring-slate-200"
                    }`}
                  />
                  {errors.confirmPassword && touched.confirmPassword && (
                    <p className="mt-2 text-xs font-medium text-red-500">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isUpdateLoading}
                  className="w-full mt-2 rounded-xl bg-black py-3.5 text-white font-semibold transition-all duration-300 hover:bg-slate-800 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdateLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="h-5 w-5 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Updating...
                    </div>
                  ) : (
                    "Update Password"
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

const mapDispatch = (dispatch) => ({
  changePassword: dispatch.auth.changePassword,
  logOut: dispatch.auth.logOut,
});

export default connect(null, mapDispatch)(ChangePassword);
